import localforage from 'localforage'
import type { OfflineCacheScope, PosCatalogKitchenPrinter, PosCatalogPayload } from 'store/posStore'
import { resolveOfflineScope, scopedPosKey } from 'store/posStore'
import { isUnpaidTakeawayKitchenEnabled } from 'pages/POS/pendingTakeaway/unpaidTakeawayKitchenCapability'

const idb = localforage.createInstance({
  name: 'erp-pos',
  storeName: 'pos',
})

export const UNPAID_TAKEAWAY_OFFLINE_LEASE_KEY = 'pos_unpaid_takeaway_offline_lease'
export const UNPAID_TAKEAWAY_OFFLINE_LEASE_TTL_MS = 24 * 60 * 60 * 1000

export type UnpaidTakeawayOfflineLease = {
  tenantId: string
  branchId: string
  registerId: string | null
  sessionId: string | null
  unpaidKitchenEnabled: boolean
  enableKitchenPrint: boolean
  useServerKitchenPrintQueue: boolean
  hasLocalKitchenPrinter: boolean
  offlineModeEnabled: boolean
  verifiedAt: string
  expiresAt: string
}

function leaseKey(scope?: OfflineCacheScope): string {
  return scopedPosKey(UNPAID_TAKEAWAY_OFFLINE_LEASE_KEY, scope, 'branch')
}

function isLease(value: unknown): value is UnpaidTakeawayOfflineLease {
  if (!value || typeof value !== 'object') return false
  const rec = value as UnpaidTakeawayOfflineLease
  return typeof rec.branchId === 'string' && typeof rec.expiresAt === 'string'
}

export function catalogHasConfiguredLocalKitchenPrinter(
  catalog: PosCatalogPayload | null | undefined,
): boolean {
  const printers = (catalog?.kitchen_printers ?? []) as PosCatalogKitchenPrinter[]
  return printers.some((printer) => {
    if (printer?.is_active === false) return false
    const name = String(printer?.printer_name || printer?.name || '').trim()
    return name.length > 0
  })
}

export function readOfflineModeFromPlanAccess(planAccess: unknown): boolean {
  if (!planAccess || typeof planAccess !== 'object') return true
  const features = (planAccess as { features?: Record<string, unknown> }).features
  if (!features || typeof features !== 'object') return true
  if (!Object.prototype.hasOwnProperty.call(features, 'offline_mode')) return true
  return Boolean(features.offline_mode)
}

export async function rememberUnpaidTakeawayOfflineLease(params: {
  catalog: PosCatalogPayload
  branchId: string
  registerId?: string | null
  sessionId?: string | null
  offlineModeEnabled: boolean
  now?: Date
  ttlMs?: number
  scope?: OfflineCacheScope
}): Promise<UnpaidTakeawayOfflineLease | null> {
  const settings = (params.catalog.branch?.settings ?? {}) as Record<string, unknown>
  const unpaidKitchenEnabled = isUnpaidTakeawayKitchenEnabled(settings)
  const enableKitchenPrint = Boolean(settings.enable_kitchen_print)
  if (!unpaidKitchenEnabled || !enableKitchenPrint) {
    await clearUnpaidTakeawayOfflineLease({ branchId: params.branchId, ...params.scope })
    return null
  }
  const now = params.now ?? new Date()
  const ttl = params.ttlMs ?? UNPAID_TAKEAWAY_OFFLINE_LEASE_TTL_MS
  const lease: UnpaidTakeawayOfflineLease = {
    tenantId: String(resolveOfflineScope(params.scope).tenantId || ''),
    branchId: params.branchId,
    registerId: params.registerId ?? null,
    sessionId: params.sessionId ?? null,
    unpaidKitchenEnabled,
    enableKitchenPrint,
    useServerKitchenPrintQueue: Boolean(settings.use_server_kitchen_print_queue),
    hasLocalKitchenPrinter: catalogHasConfiguredLocalKitchenPrinter(params.catalog),
    offlineModeEnabled: params.offlineModeEnabled,
    verifiedAt: now.toISOString(),
    expiresAt: new Date(now.getTime() + ttl).toISOString(),
  }
  await idb.setItem(leaseKey({ ...params.scope, branchId: params.branchId }), lease)
  return lease
}

export async function getUnpaidTakeawayOfflineLease(
  scope?: OfflineCacheScope,
): Promise<UnpaidTakeawayOfflineLease | null> {
  const resolved = resolveOfflineScope(scope)
  const raw = await idb.getItem(leaseKey(resolved))
  if (!isLease(raw)) return null
  if (raw.branchId && resolved.branchId && raw.branchId !== resolved.branchId) return null
  if (raw.tenantId && resolved.tenantId && raw.tenantId !== resolved.tenantId) return null
  return raw
}

export function evaluateUnpaidTakeawayOfflineLease(
  lease: UnpaidTakeawayOfflineLease | null,
  params: {
    branchId: string | null
    tenantId?: string | null
    now?: Date
    requireLocalPrinter?: boolean
  },
): { ok: true; lease: UnpaidTakeawayOfflineLease } | { ok: false; reason: string } {
  if (!lease) return { ok: false, reason: 'lease_missing' }
  if (params.branchId && lease.branchId !== params.branchId) return { ok: false, reason: 'branch_mismatch' }
  if (params.tenantId && lease.tenantId && lease.tenantId !== params.tenantId) return { ok: false, reason: 'tenant_mismatch' }
  const now = params.now ?? new Date()
  if (Date.parse(lease.expiresAt) <= now.getTime()) return { ok: false, reason: 'lease_expired' }
  if (!lease.offlineModeEnabled) return { ok: false, reason: 'offline_mode_disabled' }
  if (!lease.unpaidKitchenEnabled || !lease.enableKitchenPrint) return { ok: false, reason: 'capability_off' }
  if (params.requireLocalPrinter && !lease.hasLocalKitchenPrinter) return { ok: false, reason: 'no_local_printer' }
  return { ok: true, lease }
}

export async function assertUnpaidTakeawayOfflineLease(params: {
  branchId: string | null
  requireLocalPrinter?: boolean
  scope?: OfflineCacheScope
}): Promise<{ ok: true; lease: UnpaidTakeawayOfflineLease } | { ok: false; reason: string }> {
  const lease = await getUnpaidTakeawayOfflineLease({ branchId: params.branchId ?? undefined, ...params.scope })
  return evaluateUnpaidTakeawayOfflineLease(lease, {
    branchId: params.branchId,
    requireLocalPrinter: params.requireLocalPrinter,
  })
}

export async function clearUnpaidTakeawayOfflineLease(scope?: OfflineCacheScope): Promise<void> {
  await idb.removeItem(leaseKey(scope))
}

export function provisionalTakeawayKitchenRef(clientUuid: string): string {
  const compact = clientUuid.replace(/-/g, '').slice(0, 8).toUpperCase()
  return `TEMP-${compact}`
}
